import { useEffect, useState } from "react";
import { api } from "../utils/api";

const VOICE_STORAGE_KEY = "dex_voice_name";

const LESSON_TYPE_OPTIONS = [
  { value: "", label: "Dex Picks" },
  { value: "conversation", label: "Conversation" },
  { value: "grammar", label: "Grammar" },
  { value: "culture", label: "Culture" },
  { value: "pronunciation", label: "Pronunciation" },
  { value: "storytelling", label: "Story" },
  { value: "dialogue", label: "Dialogue" },
  { value: "vocabulary", label: "Vocabulary" },
  { value: "slang", label: "Slang & Idioms" },
];

function getPreferredVoice() {
  if (!("speechSynthesis" in window)) return null;
  const voices = window.speechSynthesis.getVoices();
  const savedVoiceName = window.localStorage.getItem(VOICE_STORAGE_KEY);
  return (
    voices.find((voice) => voice.name === savedVoiceName) ||
    voices.find((voice) => voice.name.includes("Google US English") || voice.lang === "en-US") ||
    voices.find((voice) => voice.lang?.startsWith("en")) ||
    null
  );
}

function cleanLessonLine(line) {
  return String(line || "")
    .replace(/^#+\s*/, "")
    .replace(/^[-*]\s*/, "")
    .replace(/\*\*/g, "")
    .trim();
}

function splitLessonSections(content = "") {
  const lines = String(content)
    .split(/\r?\n/)
    .map(cleanLessonLine)
    .filter(Boolean);

  const sections = {
    vocabulary: [],
    pronunciation: [],
    examples: [],
    practice: [],
    dialogue: [],
    culture: [],
    other: [],
  };

  let current = "other";
  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.includes("vocabulary") || lower.includes("words")) { current = "vocabulary"; continue; }
    if (lower.includes("pronunciation") || lower.includes("phonetic")) { current = "pronunciation"; continue; }
    if (lower.includes("example") || lower.includes("sentence")) { current = "examples"; continue; }
    if (lower.includes("practice") || lower.includes("prompt") || lower.includes("challenge")) { current = "practice"; continue; }
    if (lower.includes("dialogue") || lower.includes("speaker") || lower.includes("person a") || lower.includes("person b")) { current = "dialogue"; continue; }
    if (lower.includes("culture") || lower.includes("tradition") || lower.includes("custom")) { current = "culture"; continue; }
    sections[current].push(line);
  }

  return sections;
}

const LESSON_INTROS = [
  (subject, level) => `Alright, pull up a chair. Today we are doing ${subject} — ${level} style.`,
  (subject, level) => `Hey, glad you are here. I have got a ${level} ${subject} lesson ready and it is a good one.`,
  (subject) => `Okay, let's get into it. ${subject}. This one is going to stick with you.`,
  (subject, level) => `No textbook energy today. Just you, me, and real ${subject}. We are at ${level} level, so here is what you need to know.`,
  (subject) => `I picked this one specifically for where you are. Today: ${subject}. Stay with me.`,
];

const VOCAB_BRIDGES = [
  "Let me hit you with the key words first. Say each one once in your head.",
  "Before anything else, here are the words that matter.",
  "These are the building blocks. Learn these and the rest will click.",
];

const PRONUNCIATION_BRIDGES = [
  "Now, how do you actually say this? Slow it down — let your mouth learn the shape.",
  "Pronunciation time. This part is worth slowing down for.",
  "Listen closely. The way your mouth moves here is what makes it sound natural.",
];

const EXAMPLE_BRIDGES = [
  "Here is what it looks like in the real world.",
  "Now watch how it sounds in an actual sentence.",
  "Real talk — here is how a native speaker would use this.",
];

const CULTURE_BRIDGES = [
  "Here is something I bet you did not know.",
  "This next part is about more than just words — it is about the people.",
  "Context matters. Here is the culture behind this.",
];

const PRACTICE_CLOSERS = [
  "Your turn. Say one sentence out loud using what you just learned. Do not overthink it.",
  "Now practice. Make one short sentence and say it aloud before you move on.",
  "Try it yourself. One sentence. Out loud. That is all I need from you right now.",
];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildTeachingScript(lesson) {
  if (!lesson) return [];
  const sections = splitLessonSections(lesson.content);
  const subject = lesson.language || "today's topic";
  const level = lesson.level || "your level";
  const lessonType = lesson.lesson_type || "";

  const introFn = pickRandom(LESSON_INTROS);
  const script = [
    introFn(subject, level),
    `Today's lesson: ${lesson.title}.`,
  ];

  if (lessonType === "culture" && sections.culture.length) {
    script.push(pickRandom(CULTURE_BRIDGES));
    script.push(sections.culture.slice(0, 3).join(". "));
  }

  if (lessonType === "storytelling" && sections.other.length) {
    script.push("Here is a short story. Listen for familiar patterns.");
    script.push(sections.other.slice(0, 4).join(" "));
  }

  if (lessonType === "dialogue" && sections.dialogue.length) {
    script.push("I am going to walk you through a real exchange now. Picture yourself in this conversation.");
    script.push(sections.dialogue.slice(0, 6).join(". "));
  }

  if (sections.vocabulary.length) {
    script.push(pickRandom(VOCAB_BRIDGES));
    script.push(sections.vocabulary.slice(0, 5).join(". "));
    script.push("Which one felt new? That is the one to drill.");
  }

  if (sections.pronunciation.length) {
    script.push(pickRandom(PRONUNCIATION_BRIDGES));
    script.push(sections.pronunciation.slice(0, 3).join(". "));
  }

  if (sections.examples.length) {
    script.push(pickRandom(EXAMPLE_BRIDGES));
    script.push(sections.examples.slice(0, 3).join(". "));
    script.push("Notice the pattern. The goal is not perfection — it is recognition.");
  }

  const extra = sections.other.filter((_, i) => lessonType !== "storytelling" || i > 3).slice(0, 2).join(". ");
  if (extra) {
    script.push(`One thing I want you to remember from this lesson: ${extra}`);
  }

  if (sections.practice.length) {
    script.push(pickRandom(PRACTICE_CLOSERS));
    script.push(sections.practice.slice(0, 2).join(". "));
  } else {
    script.push(pickRandom(PRACTICE_CLOSERS));
  }

  script.push("When you are ready, tap Start Quiz Mode and I will check what stuck.");
  return script;
}

export default function LearningHub() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [history, setHistory] = useState({ lessons: [], quizAttempts: [], progress: null });
  const [lesson, setLesson] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [selectedLessonType, setSelectedLessonType] = useState("");
  const speechSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  function speakText(text, options = {}) {
    if (!speechSupported || !text?.trim()) {
      setError("This browser cannot play Dex lesson audio.");
      return;
    }

    window.speechSynthesis.cancel();
    if (typeof window.speechSynthesis.resume === "function") {
      window.speechSynthesis.resume();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = options.rate || 0.86;
    utterance.pitch = options.pitch || 0.95;
    utterance.volume = 1;
    const preferredVoice = getPreferredVoice();
    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => {
      setSpeaking(false);
      setError("Dex could not play lesson audio. Try tapping Play Lesson again.");
    };
    window.speechSynthesis.speak(utterance);
  }

  function speakSequence(parts, options = {}) {
    if (!speechSupported || !parts?.length) {
      setError("This browser cannot play Dex lesson audio.");
      return;
    }

    window.speechSynthesis.cancel();
    if (typeof window.speechSynthesis.resume === "function") {
      window.speechSynthesis.resume();
    }

    const preferredVoice = getPreferredVoice();
    let index = 0;
    setSpeaking(true);

    const playNext = () => {
      const text = parts[index];
      if (!text) {
        setSpeaking(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = options.rate || 0.82;
      utterance.pitch = options.pitch || 0.98;
      utterance.volume = 1;
      if (preferredVoice) utterance.voice = preferredVoice;
      utterance.onend = () => {
        index += 1;
        window.setTimeout(playNext, options.pauseMs ?? 450);
      };
      utterance.onerror = () => {
        setSpeaking(false);
        setError("Dex could not finish the spoken lesson. Try Teach It again.");
      };
      window.speechSynthesis.speak(utterance);
    };

    playNext();
  }

  function stopSpeech() {
    if (!speechSupported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }

  function lessonText(currentLesson = lesson) {
    if (!currentLesson) return "";
    return `${currentLesson.title}. ${currentLesson.content}`;
  }

  function teachLesson() {
    speakSequence(buildTeachingScript(lesson));
  }

  async function loadHistory() {
    try {
      const data = await api.getLearningHistory();
      setHistory(data);
    } catch (err) {
      setError(err?.message || "Failed to load learning history.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadHistory();
  }, []);

  async function handleDailyLesson() {
    setBusy("lesson");
    setError("");
    try {
      const body = selectedLessonType ? { lessonType: selectedLessonType } : {};
      const data = await api.getDailyLesson(body);
      setLesson(data.lesson);
      setQuiz(null);
      setQuizResult(null);
      await loadHistory();
    } catch (err) {
      setError(err?.message || "Dex could not create a lesson right now.");
    } finally {
      setBusy("");
    }
  }

  async function handleQuiz() {
    setBusy("quiz");
    setError("");
    try {
      const data = await api.createLearningQuiz();
      setQuiz(data.quiz);
      setAnswers(new Array(data.quiz?.questions?.length || 0).fill(""));
      setQuizResult(null);
    } catch (err) {
      setError(err?.message || "Dex could not create a quiz right now.");
    } finally {
      setBusy("");
    }
  }

  async function handleSubmitQuiz() {
    if (!quiz) return;
    setBusy("submit");
    setError("");
    try {
      const data = await api.submitLearningQuiz({ quiz, answers });
      setQuizResult(data);
      await loadHistory();
      try {
        const nextLesson = await api.getDailyLesson();
        setLesson(nextLesson.lesson);
        setQuiz(null);
        setAnswers([]);
      } catch {
        setError("Quiz scored, but Dex could not build the next lesson yet. Tap Get Daily Lesson to try again.");
      }
    } catch (err) {
      setError(err?.message || "Dex could not score your quiz right now.");
    } finally {
      setBusy("");
    }
  }

  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-white">Learning Hub</h2>
        <p className="text-sm text-gray-400">Daily lessons, quiz mode, lesson history, and progress tracking live here now.</p>
      </div>

      {error && (
        <div className="rounded-md border border-red-700/60 bg-red-900/30 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleDailyLesson}
          disabled={busy === "lesson"}
          className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-light disabled:opacity-60"
        >
          {busy === "lesson" ? "Building lesson..." : "Get Daily Lesson"}
        </button>
        <button
          type="button"
          onClick={handleQuiz}
          disabled={busy === "quiz"}
          className="rounded-md border border-gray-700 px-4 py-2 text-sm font-semibold text-gray-100 hover:border-gray-500 disabled:opacity-60"
        >
          {busy === "quiz" ? "Building quiz..." : "Start Quiz Mode"}
        </button>
      </div>

      <div>
        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Lesson Type</p>
        <div className="flex flex-wrap gap-2">
          {LESSON_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSelectedLessonType(opt.value)}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition-all ${
                selectedLessonType === opt.value
                  ? "bg-brand border-brand text-white"
                  : "border-gray-700 text-gray-400 hover:border-gray-500"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-gray-800 bg-gray-950 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Quiz Attempts</div>
          <div className="mt-1 text-white font-medium">{history.progress?.attempts ?? 0}</div>
        </div>
        <div className="rounded-md border border-gray-800 bg-gray-950 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Average Score</div>
          <div className="mt-1 text-white font-medium">
            {history.progress?.averageScore != null ? `${history.progress.averageScore}%` : "No quizzes yet"}
          </div>
        </div>
        <div className="rounded-md border border-gray-800 bg-gray-950 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Saved Lessons</div>
          <div className="mt-1 text-white font-medium">{history.lessons?.length ?? 0}</div>
        </div>
        <div className="rounded-md border border-gray-800 bg-gray-950 px-4 py-3 sm:col-span-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Learning Streak</div>
          <div className="mt-1 text-white font-medium">
            {history.progress?.streak ? `${history.progress.streak} day${history.progress.streak === 1 ? "" : "s"}` : "Start today"}
          </div>
          {history.reminders?.enabled && history.reminders?.time && (
            <div className="mt-1 text-xs text-gray-400">Reminder set for {history.reminders.time}</div>
          )}
        </div>
      </div>

      {history.nextLesson && (
        <div className="rounded-lg border border-blue-800/60 bg-blue-900/20 p-4">
          <div className="text-xs uppercase tracking-wide text-blue-300">Dex Recommends</div>
          <h3 className="text-lg font-semibold text-white mt-1">{history.nextLesson.topic}</h3>
          <p className="text-sm text-blue-100 mt-2">{history.nextLesson.reason}</p>
          <button
            type="button"
            onClick={handleDailyLesson}
            disabled={busy === "lesson"}
            className="mt-3 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-light disabled:opacity-60"
          >
            {busy === "lesson" ? "Building lesson..." : "Use This Next Lesson"}
          </button>
        </div>
      )}

      {lesson && (
        <div className="rounded-lg border border-gray-800 bg-gray-950 p-4 space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              {lesson.language} • {lesson.level}
              {lesson.lesson_type && lesson.lesson_type !== "daily" && (
                <span className="ml-2 text-brand capitalize">{lesson.lesson_type}</span>
              )}
            </div>
            <h3 className="text-lg font-semibold text-white mt-1">{lesson.title}</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={teachLesson}
              disabled={!speechSupported}
              className="rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-light disabled:opacity-60"
            >
              {speaking ? "Teaching..." : "Teach It"}
            </button>
            <button
              type="button"
              onClick={() => speakText(lessonText(), { rate: 0.78, pitch: 0.94 })}
              disabled={!speechSupported}
              className="rounded-md border border-gray-700 px-3 py-2 text-sm font-semibold text-gray-100 hover:border-gray-500 disabled:opacity-60"
            >
              Read Notes
            </button>
            <button
              type="button"
              onClick={() => speakText("Hey, I'm Dex. Lesson audio is ready. Tap Teach It and I will walk you through the lesson like a real tutor.")}
              disabled={!speechSupported}
              className="rounded-md border border-gray-700 px-3 py-2 text-sm font-semibold text-gray-100 hover:border-gray-500 disabled:opacity-60"
            >
              Test Voice
            </button>
            <button
              type="button"
              onClick={stopSpeech}
              disabled={!speaking}
              className="rounded-md border border-gray-700 px-3 py-2 text-sm font-semibold text-gray-100 hover:border-gray-500 disabled:opacity-60"
            >
              Stop
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-gray-200 font-sans">{lesson.content}</pre>
        </div>
      )}

      {quiz && (
        <div className="rounded-lg border border-gray-800 bg-gray-950 p-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">{quiz.language || "Language quiz"}</div>
            <h3 className="text-lg font-semibold text-white mt-1">{quiz.title || "Quiz"}</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => speakText(`${quiz.title || "Quiz"}. ${(quiz.questions || []).map((question, index) => `Question ${index + 1}. ${question.question}. Choices: ${(question.choices || []).join(", ")}.`).join(" ")}`)}
              disabled={!speechSupported}
              className="rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-light disabled:opacity-60"
            >
              Read Quiz
            </button>
            <button
              type="button"
              onClick={stopSpeech}
              disabled={!speaking}
              className="rounded-md border border-gray-700 px-3 py-2 text-sm font-semibold text-gray-100 hover:border-gray-500 disabled:opacity-60"
            >
              Stop
            </button>
          </div>

          <div className="space-y-4">
            {(quiz.questions || []).map((question, index) => (
              <div key={`${question.question}-${index}`} className="rounded-md border border-gray-800 bg-gray-900 p-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-white">{index + 1}. {question.question}</p>
                  <button
                    type="button"
                    onClick={() => speakText(`Question ${index + 1}. ${question.question}. Choices: ${(question.choices || []).join(", ")}.`)}
                    disabled={!speechSupported}
                    className="shrink-0 rounded-md border border-gray-700 px-2 py-1 text-xs font-semibold text-gray-100 hover:border-gray-500 disabled:opacity-60"
                  >
                    Read
                  </button>
                </div>
                <div className="mt-3 grid gap-2">
                  {(question.choices || []).map((choice) => (
                    <label key={choice} className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="radio"
                        name={`quiz-${index}`}
                        checked={answers[index] === choice}
                        onChange={() => setAnswers((prev) => {
                          const next = [...prev];
                          next[index] = choice;
                          return next;
                        })}
                      />
                      <span>{choice}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleSubmitQuiz}
            disabled={busy === "submit"}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-light disabled:opacity-60"
          >
            {busy === "submit" ? "Scoring and building next lesson..." : "Submit Quiz"}
          </button>
        </div>
      )}

      {quizResult && (
        <div className="rounded-lg border border-green-700/50 bg-green-900/20 p-4 space-y-3">
          <h3 className="text-lg font-semibold text-white">Quiz Results</h3>
          <p className="text-sm text-green-200">
            You scored {quizResult.score} out of {quizResult.totalQuestions} ({quizResult.percentage}%).
          </p>
          {lesson && (
            <p className="text-sm text-green-100">
              Dex built your next lesson below based on that quiz.
            </p>
          )}
          <div className="space-y-2">
            {quizResult.results.map((result, index) => (
              <div key={`${result.question}-${index}`} className="rounded-md border border-gray-800 bg-gray-950 p-3 text-sm">
                <p className="text-white font-medium">{result.question}</p>
                <p className={result.correct ? "text-green-300 mt-1" : "text-red-300 mt-1"}>
                  Your answer: {result.userAnswer || "No answer"}
                </p>
                {!result.correct && (
                  <p className="text-gray-300 mt-1">Correct answer: {result.correctAnswer}</p>
                )}
                {result.explanation && (
                  <p className="text-gray-400 mt-1">{result.explanation}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && history.lessons?.length > 0 && (
        <div className="rounded-lg border border-gray-800 bg-gray-950 p-4">
          <h3 className="text-lg font-semibold text-white mb-3">Saved Lesson History</h3>
          <div className="space-y-2">
            {history.lessons.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setLesson(item)}
                className="w-full text-left rounded-md border border-gray-800 bg-gray-900 px-3 py-3 hover:border-gray-700"
              >
                <div className="text-sm font-medium text-white">{item.title}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {item.language || "General"} • {item.level || "mixed"} • {new Date(item.created_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
